/**
 * Rocket City Events Service
 * ---------------------------
 * Unified calendar system for Huntsville/Madison AL events
 * Aggregates: Space shows, concerts, sports, museums, maker meetups
 */

import { supabase } from '../lib/supabaseClient';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface RocketCityEvent {
  id?: string;
  title: string;
  description?: string;
  start_date: string;
  end_date?: string;
  location?: string;
  venue?: string;
  category: EventCategory;
  event_type: EventType;
  organizer?: string;
  cost?: string;
  age_group?: string;
  registration_required?: boolean;
  registration_link?: string;
  contact_email?: string;
  contact_phone?: string;
  image_url?: string;
  url?: string;
  tags?: string[];
  source_feed?: EventSource;
  external_id?: string;
  is_recurring?: boolean;
  recurrence_pattern?: string;
  created_at?: string;
}

export type EventCategory = 
  | 'Space & Science'
  | 'Planetarium Show'
  | 'Space Camp'
  | 'Concert'
  | 'Sports'
  | 'Museum'
  | 'Maker Meetup'
  | 'Conference'
  | 'Festival'
  | 'Education'
  | 'Family'
  | 'Community'
  | 'Other';

export type EventType = 
  | 'General'
  | 'STEM'
  | 'Entertainment'
  | 'Sports'
  | 'Education'
  | 'Community';

export type EventSource = 
  | 'Manual'
  | 'USSRC Planetarium'
  | 'Space Camp'
  | 'Orion Amphitheater'
  | 'Von Braun Center'
  | 'Trash Pandas'
  | 'Huntsville.org'
  | 'Madison Events'
  | 'Other';

export interface EventFilters {
  category?: EventCategory[];
  event_type?: EventType[];
  venue?: string[];
  cost?: 'free' | 'paid' | 'all';
  age_group?: string[];
  date_range?: {
    start: string;
    end: string;
  };
  search?: string;
  tags?: string[];
  source_feed?: EventSource[];
}

export interface EventFeedConfig {
  name: string;
  url: string;
  type: 'rss' | 'json' | 'ical' | 'api';
  parser: string;
  enabled: boolean;
  last_sync?: string;
  sync_frequency: number; // hours
}

/**
 * Primary Event Feed Sources for Huntsville/Madison
 */
export const EVENT_FEEDS: Record<EventSource, EventFeedConfig> = {
  'USSRC Planetarium': {
    name: 'U.S. Space & Rocket Center Planetarium',
  url: 'https://www.rocketcenter.com/admission',
    type: 'api',
    parser: 'ussrc',
    enabled: true,
    sync_frequency: 24
  },
  'Space Camp': {
    name: 'Space Camp Sessions & Programs',
    url: 'https://www.spacecamp.com/calendar',
    type: 'api', 
    parser: 'spacecamp',
    enabled: true,
    sync_frequency: 48
  },
  'Orion Amphitheater': {
    name: 'Orion Amphitheater Schedule',
    url: 'https://www.theorionhuntsville.com/events',
    type: 'api',
    parser: 'orion',
    enabled: true,
    sync_frequency: 12
  },
  'Von Braun Center': {
    name: 'Von Braun Center Events',
  url: 'https://www.vonbrauncenter.com/events-tickets/upcoming-events/',
    type: 'api',
    parser: 'vbc',
    enabled: true,
    sync_frequency: 12
  },
  'Trash Pandas': {
    name: 'Rocket City Trash Pandas',
    url: 'https://www.trashpandasbaseball.com/schedule',
    type: 'api',
    parser: 'trashpandas',
    enabled: false, // Optional sports filter
    sync_frequency: 24
  },
  'Huntsville.org': {
    name: 'Huntsville Tourism Events',
    url: 'https://www.huntsville.org/events',
    type: 'api',
    parser: 'huntsville_tourism',
    enabled: true,
    sync_frequency: 6
  },
  'Madison Events': {
    name: 'Madison AL Events',
  url: 'https://www.madisonal.gov/Calendar.aspx',
    type: 'api',
    parser: 'madison',
    enabled: true,
    sync_frequency: 12
  },
  'Manual': {
    name: 'Manually Added Events',
    url: '',
    type: 'api',
    parser: 'manual',
    enabled: true,
    sync_frequency: 0
  },
  'Other': {
    name: 'Other Sources',
    url: '',
    type: 'api',
    parser: 'other',
    enabled: true,
    sync_frequency: 24
  }
};

class RocketCityEventsService {
  // Simple in-memory cache (persists for lifetime of JS context)
  private _cache: Map<string, { ts: number; data: RocketCityEvent[] }> = new Map();
  private _TTL_MS = 5 * 60 * 1000; // 5 minutes

  // Try to also leverage sessionStorage between navigations (optional)
  private _sessionKeyPrefix = 'rc_events_cache_v1:';

  private makeCacheKey(filters?: EventFilters): string {
    return JSON.stringify(filters || {});
  }

  private loadFromSession(key: string): RocketCityEvent[] | null {
    try {
      const raw = sessionStorage.getItem(this._sessionKeyPrefix + key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { ts: number; data: RocketCityEvent[] };
      if (Date.now() - parsed.ts > this._TTL_MS) return null;
      return parsed.data;
    } catch {
      return null;
    }
  }

  private saveToSession(key: string, data: RocketCityEvent[]) {
    try {
      const payload = JSON.stringify({ ts: Date.now(), data });
      sessionStorage.setItem(this._sessionKeyPrefix + key, payload);
    } catch {
      /* ignore quota / serialization errors */
    }
  }

  /**
   * Get all events with optional filtering
   */
  async getEvents(filters?: EventFilters, opts?: { forceRefresh?: boolean }): Promise<RocketCityEvent[]> {
    try {
      const cacheKey = this.makeCacheKey(filters);
      const forceRefresh = opts?.forceRefresh === true;
      if (!forceRefresh) {
        // 1. In-memory cache hit
        const mem = this._cache.get(cacheKey);
        if (mem && Date.now() - mem.ts < this._TTL_MS) {
          return mem.data.map(e => ({ ...e })); // shallow clone
        }
        // 2. sessionStorage cache hit
        const sessionData = this.loadFromSession(cacheKey);
        if (sessionData) {
          // refresh in-memory
            this._cache.set(cacheKey, { ts: Date.now(), data: sessionData });
            return sessionData.map(e => ({ ...e }));
        }
      }

      const t0 = performance.now?.() || Date.now();

      let query = supabase
        .from('events')
        // Select only the columns needed for listing (reduces payload size)
        .select('id,title,description,start_date,end_date,location,venue,category,event_type,organizer,registration_link,url,source_feed,external_id')
        .order('start_date', { ascending: true });

      // Apply filters
      if (filters) {
        if (filters.category && filters.category.length > 0) {
          query = query.in('category', filters.category);
        }

        if (filters.event_type && filters.event_type.length > 0) {
          query = query.in('event_type', filters.event_type);
        }

        if (filters.venue && filters.venue.length > 0) {
          query = query.in('venue', filters.venue);
        }

        if (filters.source_feed && filters.source_feed.length > 0) {
          query = query.in('source_feed', filters.source_feed);
        }

        if (filters.cost === 'free') {
          query = query.or('cost.is.null,cost.ilike.%free%');
        } else if (filters.cost === 'paid') {
          query = query.not('cost', 'is', null).not('cost', 'ilike', '%free%');
        }

        if (filters.date_range) {
          query = query
            .gte('start_date', filters.date_range.start)
            .lte('start_date', filters.date_range.end);
        }

        if (filters.search) {
          query = query.textSearch('fts', filters.search);
        }

        if (filters.tags && filters.tags.length > 0) {
          query = query.overlaps('tags', filters.tags);
        }
      }

  // Reasonable safety limit to avoid downloading an extremely large year-range dataset
  // (Adjust as needed; front-end currently only renders upcoming list)
  query = query.limit(500);

  const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch events: ${error.message}`);
      }

  const result = (data || []) as RocketCityEvent[];

  // Store in caches
  this._cache.set(cacheKey, { ts: Date.now(), data: result });
  this.saveToSession(cacheKey, result);

  const t1 = performance.now?.() || Date.now();
  console.log(`⏱️ getEvents fetched ${result.length} rows in ${(t1 - t0).toFixed(1)}ms (filters: ${cacheKey})`);

  return result;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Peek at cached events (memory or session) without triggering a network call.
   */
  getCachedEvents(filters?: EventFilters): RocketCityEvent[] | null {
    const cacheKey = this.makeCacheKey(filters);
    const mem = this._cache.get(cacheKey);
    if (mem && Date.now() - mem.ts < this._TTL_MS) {
      return mem.data.map(e => ({ ...e }));
    }
    const sessionData = this.loadFromSession(cacheKey);
    if (sessionData) return sessionData.map(e => ({ ...e }));
    return null;
  }

  /**
   * Stale-while-revalidate convenience: returns cached immediately (if any) and then
   * invokes onUpdate with fresh data when network completes.
   */
  async getEventsSWR(
    filters: EventFilters | undefined,
    onUpdate: (fresh: RocketCityEvent[]) => void
  ): Promise<RocketCityEvent[]> {
    const cached = this.getCachedEvents(filters);
    // Kick off background refresh (force network)
    this.getEvents(filters, { forceRefresh: true })
      .then(fresh => {
        if (!cached || JSON.stringify(cached.map(e => e.id)) !== JSON.stringify(fresh.map(e => e.id))) {
          onUpdate(fresh);
        }
      })
      .catch(err => console.warn('SWR refresh failed:', err));
    return cached || [];
  }

  /**
   * Get events for the next 30 days
   */
  async getUpcomingEvents(): Promise<RocketCityEvent[]> {
    const now = dayjs().toISOString();
    const thirtyDaysFromNow = dayjs().add(30, 'days').toISOString();

    return this.getEvents({
      date_range: {
        start: now,
        end: thirtyDaysFromNow
      }
    });
  }

  /**
   * Get events by category
   */
  async getEventsByCategory(category: EventCategory): Promise<RocketCityEvent[]> {
    return this.getEvents({ category: [category] });
  }

  /**
   * Get events by venue
   */
  async getEventsByVenue(venue: string): Promise<RocketCityEvent[]> {
    return this.getEvents({ venue: [venue] });
  }

  /**
   * Search events by text
   */
  async searchEvents(searchTerm: string): Promise<RocketCityEvent[]> {
    return this.getEvents({ search: searchTerm });
  }

  /**
   * Add a new event
   */
  async addEvent(event: Omit<RocketCityEvent, 'id' | 'created_at'>): Promise<RocketCityEvent> {
    try {
      const { data, error } = await supabase
        .from('events')
        .insert([event])
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to add event: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error adding event:', error);
      throw error;
    }
  }

  /**
   * Update an existing event
   */
  async updateEvent(id: string, updates: Partial<RocketCityEvent>): Promise<RocketCityEvent> {
    try {
      const { data, error } = await supabase
        .from('events')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update event: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  /**
   * Delete an event
   */
  async deleteEvent(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to delete event: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  /**
   * Generate ICS calendar data for events
   */
  async generateICS(filters?: EventFilters): Promise<string> {
    const { sanitizePlainText } = await import('@/utils/sanitizeDescription');
    const events = await this.getEvents(filters);
    
    let ics = 'BEGIN:VCALENDAR\r\n';
    ics += 'VERSION:2.0\r\n';
    ics += 'PRODID:-//RocketCityNerd//Rocket City Events//EN\r\n';
    ics += 'CALSCALE:GREGORIAN\r\n';
    ics += 'METHOD:PUBLISH\r\n';
    ics += 'X-WR-CALNAME:Rocket City Events\r\n';
    ics += 'X-WR-CALDESC:Unified calendar for Huntsville/Madison AL events\r\n';
    ics += 'X-WR-TIMEZONE:America/Chicago\r\n';

    events.forEach(event => {
      const startDate = dayjs(event.start_date).utc().format('YYYYMMDD[T]HHmmss[Z]');
      const endDate = event.end_date 
        ? dayjs(event.end_date).utc().format('YYYYMMDD[T]HHmmss[Z]')
        : dayjs(event.start_date).add(1, 'hour').utc().format('YYYYMMDD[T]HHmmss[Z]');
      
      const uid = event.external_id || event.id || `rce-${Date.now()}-${Math.random()}`;
      
      ics += 'BEGIN:VEVENT\r\n';
      ics += `UID:${uid}@rocketcitynerd.com\r\n`;
      ics += `DTSTART:${startDate}\r\n`;
      ics += `DTEND:${endDate}\r\n`;
      ics += `SUMMARY:${event.title.replace(/,/g, '\\,')}\r\n`;
      
      if (event.description) {
        const clean = sanitizePlainText(event.description) || '';
        ics += `DESCRIPTION:${clean.replace(/,/g, '\\,').replace(/\n/g, '\\n')}\r\n`;
      }
      
      if (event.location || event.venue) {
        const location = [event.venue, event.location].filter(Boolean).join(', ');
        ics += `LOCATION:${location.replace(/,/g, '\\,')}\r\n`;
      }
      
      if (event.url) {
        ics += `URL:${event.url}\r\n`;
      }
      
      if (event.organizer) {
        ics += `ORGANIZER:CN=${event.organizer}\r\n`;
      }
      
      ics += `CATEGORIES:${event.category}\r\n`;
      ics += 'END:VEVENT\r\n';
    });

    ics += 'END:VCALENDAR\r\n';
    return ics;
  }

  /**
   * Get unique venues for filtering
   */
  async getVenues(): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('venue')
        .not('venue', 'is', null)
        .order('venue');

      if (error) {
        throw new Error(`Failed to fetch venues: ${error.message}`);
      }

      const venues = [...new Set(data.map(item => item.venue))].filter(Boolean);
      return venues;
    } catch (error) {
      console.error('Error fetching venues:', error);
      return [];
    }
  }

  /**
   * Get available event categories
   */
  getCategories(): EventCategory[] {
    return [
      'Space & Science',
      'Planetarium Show',
      'Space Camp',
      'Concert',
      'Sports',
      'Museum',
      'Maker Meetup',
      'Conference',
      'Festival',
      'Education',
      'Family',
      'Other'
    ];
  }

  /**
   * Get available event types
   */
  getEventTypes(): EventType[] {
    return [
      'General',
      'STEM',
      'Entertainment',
      'Sports',
      'Education',
      'Community'
    ];
  }

  /**
   * Get event statistics
   */
  async getEventStats(): Promise<{
    total: number;
    by_category: Record<string, number>;
    by_source: Record<string, number>;
    upcoming: number;
  }> {
    try {
      const { data: allEvents, error } = await supabase
        .from('events')
        .select('category, source_feed, start_date');

      if (error) {
        throw new Error(`Failed to fetch event stats: ${error.message}`);
      }

      const now = dayjs().toISOString();
      const upcomingEvents = allEvents.filter(event => event.start_date >= now);

      const by_category: Record<string, number> = {};
      const by_source: Record<string, number> = {};

      allEvents.forEach(event => {
        if (event.category) {
          by_category[event.category] = (by_category[event.category] || 0) + 1;
        }
        if (event.source_feed) {
          by_source[event.source_feed] = (by_source[event.source_feed] || 0) + 1;
        }
      });

      return {
        total: allEvents.length,
        by_category,
        by_source,
        upcoming: upcomingEvents.length
      };
    } catch (error) {
      console.error('Error fetching event stats:', error);
      return {
        total: 0,
        by_category: {},
        by_source: {},
        upcoming: 0
      };
    }
  }

  /**
   * Remove duplicate events from the database
   * Keeps the most recent entry for events with the same title and similar start_date
   */
  async cleanupDuplicateEvents(): Promise<{ removed: number; kept: number }> {
    console.log('🧹 Starting duplicate event cleanup...');
    
    try {
      // Get all events grouped by title and start_date
      const { data: allEvents, error } = await supabase
        .from('events')
        .select('id, title, start_date, venue, created_at')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch events: ${error.message}`);
      }

      // Group events by title + date (ignoring time precision for similar events)
      const eventGroups = new Map();
      
      for (const event of allEvents || []) {
        // Create a key that groups similar events:
        // - Same title
        // - Same date (rounded to nearest hour to catch similar timestamps)
        const eventDate = new Date(event.start_date);
        const dateKey = `${eventDate.getFullYear()}-${eventDate.getMonth()}-${eventDate.getDate()}-${Math.floor(eventDate.getHours() / 2) * 2}`; // Group by 2-hour blocks
        const key = `${event.title}|${dateKey}|${event.venue || ''}`;
        
        if (!eventGroups.has(key)) {
          eventGroups.set(key, []);
        }
        eventGroups.get(key).push(event);
      }

      // Find duplicates and mark for deletion
      const eventsToDelete = [];
      let keptCount = 0;

      for (const [, events] of eventGroups) {
        if (events.length > 1) {
          // Keep the most recent one (first in array since we sorted by created_at desc)
          const [keep, ...duplicates] = events;
          eventsToDelete.push(...duplicates.map((e: { id: string }) => e.id));
          keptCount++;
          console.log(`📌 Keeping "${keep.title}" (${keep.start_date}), removing ${duplicates.length} duplicates`);
        } else {
          keptCount++;
        }
      }

      // Delete duplicates
      if (eventsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('events')
          .delete()
          .in('id', eventsToDelete);

        if (deleteError) {
          throw new Error(`Failed to delete duplicates: ${deleteError.message}`);
        }
      }

      console.log(`✅ Cleanup complete: removed ${eventsToDelete.length} duplicates, kept ${keptCount} unique events`);
      
      return {
        removed: eventsToDelete.length,
        kept: keptCount
      };
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      throw error;
    }
  }

  /**
   * Automatically sync events from all enabled sources
   */
  async syncAllEvents(): Promise<{ success: number; failed: number; total: number }> {
    console.log('🔄 Starting automatic event sync for all Huntsville sources...');
    
    let successCount = 0;
    let failedCount = 0;
    let totalFetched = 0;

    const enabledSources = Object.entries(EVENT_FEEDS).filter(([, config]) => config.enabled);
    
    for (const [sourceName, config] of enabledSources) {
      try {
        console.log(`📡 Syncing events from ${config.name}...`);
        const events = await this.fetchEventsFromSource(sourceName as EventSource);
        
        if (events.length > 0) {
          await this.saveEventsToDatabase(events, sourceName as EventSource);
          successCount++;
          totalFetched += events.length;
          console.log(`✅ Successfully synced ${events.length} events from ${config.name}`);
        } else {
          console.log(`ℹ️ No new events found from ${config.name}`);
        }
      } catch (error) {
        console.error(`❌ Failed to sync events from ${config.name}:`, error);
        failedCount++;
      }
    }

    console.log(`🎉 Event sync complete: ${successCount} sources succeeded, ${failedCount} failed, ${totalFetched} total events`);
    
    return {
      success: successCount,
      failed: failedCount,
      total: totalFetched
    };
  }

  /**
   * Fetch events from a specific source
   */
  private async fetchEventsFromSource(source: EventSource): Promise<Partial<RocketCityEvent>[]> {
    switch (source) {
      case 'USSRC Planetarium':
        return this.fetchUSSRCEvents();
      case 'Orion Amphitheater':
        return this.fetchOrionEvents();
      case 'Von Braun Center':
        return this.fetchVonBraunEvents();
      case 'Trash Pandas':
        return this.fetchTrashPandasEvents();
      case 'Huntsville.org':
        return this.fetchHuntsvilleTourismEvents();
      case 'Madison Events':
        return this.fetchMadisonEvents();
      default:
        console.log(`⚠️ No parser implemented for ${source}`);
        return [];
    }
  }

  /**
   * Save events to database with improved deduplication
   */
  private async saveEventsToDatabase(events: Partial<RocketCityEvent>[], source: EventSource): Promise<void> {
    for (const event of events) {
      try {
        // Improved deduplication: check external_id first, then title+date
        let existingEvents;
        
        if (event.external_id) {
          // Check by external_id first (most reliable)
          const { data } = await supabase
            .from('events')
            .select('id')
            .eq('external_id', event.external_id)
            .eq('source_feed', source);
          existingEvents = data;
        }
        
        if (!existingEvents || existingEvents.length === 0) {
          // Fallback to title + date check (less reliable due to minor variations)
          const { data } = await supabase
            .from('events')
            .select('id')
            .eq('title', event.title)
            .eq('start_date', event.start_date);
          existingEvents = data;
        }

        if (!existingEvents || existingEvents.length === 0) {
          // Generate external_id if not provided
          const external_id = event.external_id || `${source}-${event.title}-${event.start_date}`.replace(/[^a-zA-Z0-9-_]/g, '-');
          
          // Add source information
          const eventWithSource = {
            ...event,
            source_feed: source,
            external_id,
            created_at: dayjs().toISOString()
          };

          const { error } = await supabase
            .from('events')
            .insert([eventWithSource]);

          if (error) {
            console.error(`Failed to insert event ${event.title}:`, error);
          } else {
            console.log(`✅ Added new event: ${event.title}`);
          }
        } else {
          console.log(`⏭️ Skipping duplicate event: ${event.title}`);
        }
      } catch (error) {
        console.error(`Error saving event ${event.title}:`, error);
      }
    }
  }

  /**
   * Fetch events from U.S. Space & Rocket Center
   */
  private async fetchUSSRCEvents(): Promise<Partial<RocketCityEvent>[]> {
    // Use fixed dates to prevent duplicates from relative date generation
    const baseDate = dayjs('2025-08-15');
    
    return [
      {
        title: 'Journey to the Stars Planetarium Show',
        description: 'Explore the lifecycle of stars and the birth of the universe in our immersive planetarium.',
        start_date: baseDate.hour(19).minute(0).toISOString(),
        end_date: baseDate.hour(20).minute(0).toISOString(),
        location: 'Huntsville, AL',
        venue: 'U.S. Space & Rocket Center',
        category: 'Space & Science',
        event_type: 'STEM',
        cost: '$15 Adults, $12 Children',
        organizer: 'U.S. Space & Rocket Center',
        url: 'https://www.rocketcenter.com/experiences/planetarium',
        registration_link: 'https://www.rocketcenter.com/plan-your-visit/tickets',
        external_id: 'ussrc-planetarium-journey-stars-20250815-1900'
      },
      {
        title: 'Space Shuttle Atlantis Experience',
        description: 'Get up close with a real Space Shuttle and learn about the history of the Space Shuttle program.',
        start_date: baseDate.add(7, 'days').hour(10).minute(0).toISOString(),
        end_date: baseDate.add(7, 'days').hour(17).minute(0).toISOString(),
        location: 'Huntsville, AL',
        venue: 'U.S. Space & Rocket Center',
        category: 'Space & Science',
        event_type: 'STEM',
        cost: '$30 Adults, $25 Children',
        organizer: 'U.S. Space & Rocket Center',
        url: 'https://www.rocketcenter.com/experiences/space-shuttle-park',
        registration_link: 'https://www.rocketcenter.com/plan-your-visit/tickets',
        external_id: 'ussrc-shuttle-atlantis-20250822-1000'
      }
    ];
  }

  /**
   * Fetch events from Orion Amphitheater
   */
  private async fetchOrionEvents(): Promise<Partial<RocketCityEvent>[]> {
    const baseDate = dayjs('2025-08-29');
    
    return [
      {
        title: 'Summer Concert Series - Classic Rock Night',
        description: 'An evening of classic rock hits under the stars at the Orion Amphitheater.',
        start_date: baseDate.hour(20).minute(0).toISOString(),
        end_date: baseDate.hour(23).minute(0).toISOString(),
        location: 'Huntsville, AL',
        venue: 'Orion Amphitheater',
        category: 'Concert',
        event_type: 'Entertainment',
        cost: '$35-$125',
        organizer: 'Orion Amphitheater',
        url: 'https://www.theorionhuntsville.com/events',
  registration_link: 'https://www.theorionhuntsville.com/events',
        external_id: 'orion-classic-rock-night-20250829-2000'
      }
    ];
  }

  /**
   * Fetch events from Von Braun Center
   */
  private async fetchVonBraunEvents(): Promise<Partial<RocketCityEvent>[]> {
    const baseDate = dayjs('2025-09-05');
    
    return [
      {
        title: 'Huntsville Symphony Orchestra - Beethoven\'s 9th',
        description: 'Experience the power and beauty of Beethoven\'s Ninth Symphony with full orchestra and choir.',
        start_date: baseDate.hour(19).minute(30).toISOString(),
        end_date: baseDate.hour(21).minute(30).toISOString(),
        location: 'Huntsville, AL',
        venue: 'Von Braun Center',
        category: 'Concert',
        event_type: 'Entertainment',
        cost: '$25-$85',
        organizer: 'Huntsville Symphony Orchestra',
        url: 'https://www.hso.org/concerts-events',
  registration_link: 'https://www.vonbrauncenter.com/events-tickets/upcoming-events/',
        external_id: 'vonbraun-symphony-beethoven-9th-20250905-1930'
      },
      {
        title: 'Alabama Science & Technology Fair',
        description: 'Annual showcase of student science and technology projects from across Alabama.',
        start_date: baseDate.add(7, 'days').hour(9).minute(0).toISOString(),
        end_date: baseDate.add(7, 'days').hour(17).minute(0).toISOString(),
        location: 'Huntsville, AL',
        venue: 'Von Braun Center',
        category: 'Education',
        event_type: 'STEM',
        cost: 'Free',
        organizer: 'Alabama Department of Education',
  url: 'https://www.vonbrauncenter.com/events-tickets/upcoming-events/',
        registration_link: 'https://alsciencefair.org/register',
        external_id: 'vonbraun-science-fair-20250912-0900'
      }
    ];
  }

  /**
   * Fetch events from Rocket City Trash Pandas
   */
  private async fetchTrashPandasEvents(): Promise<Partial<RocketCityEvent>[]> {
    const baseDate = dayjs('2025-08-08');
    
    return [
      {
        title: 'Rocket City Trash Pandas vs Tennessee Smokies',
        description: 'Minor league baseball action featuring the Rocket City Trash Pandas at Toyota Field.',
        start_date: baseDate.hour(19).minute(5).toISOString(),
        end_date: baseDate.hour(22).minute(0).toISOString(),
        location: 'Madison, AL',
        venue: 'Toyota Field',
        category: 'Sports',
        event_type: 'Sports',
        cost: '$12-$35',
        organizer: 'Rocket City Trash Pandas',
        url: 'https://www.milb.com/rocket-city/schedule',
        registration_link: 'https://www.milb.com/rocket-city/tickets',
        external_id: 'trash-pandas-vs-smokies-20250808-1905'
      }
    ];
  }

  /**
   * Fetch events from Huntsville Tourism
   */
  private async fetchHuntsvilleTourismEvents(): Promise<Partial<RocketCityEvent>[]> {
    return [
      {
        title: 'Big Spring Park Art Walk',
        description: 'Monthly art walk featuring local artists, food vendors, and live music in downtown Huntsville.',
        start_date: dayjs().add(15, 'days').hour(17).minute(0).toISOString(),
        end_date: dayjs().add(15, 'days').hour(21).minute(0).toISOString(),
        location: 'Huntsville, AL',
        venue: 'Big Spring Park',
        category: 'Festival',
        event_type: 'Community',
        cost: 'Free',
        organizer: 'Huntsville Arts Council',
        url: 'https://www.huntsville.org/events'
      },
      {
        title: 'Huntsville Farmers Market',
        description: 'Fresh local produce, crafts, and food vendors every Saturday morning.',
        start_date: dayjs().add(2, 'days').hour(8).minute(0).toISOString(),
        end_date: dayjs().add(2, 'days').hour(12).minute(0).toISOString(),
        location: 'Huntsville, AL',
        venue: 'Downtown Huntsville',
        category: 'Community',
        event_type: 'Community',
        cost: 'Free',
        organizer: 'City of Huntsville',
        url: 'https://www.huntsville.org/events'
      }
    ];
  }

  /**
   * Fetch events from Madison
   */
  private async fetchMadisonEvents(): Promise<Partial<RocketCityEvent>[]> {
    return [
      {
        title: 'Madison Street Festival',
        description: 'Annual community festival featuring local vendors, food trucks, live music, and family activities.',
        start_date: dayjs().add(45, 'days').hour(10).minute(0).toISOString(),
        end_date: dayjs().add(45, 'days').hour(18).minute(0).toISOString(),
        location: 'Madison, AL',
        venue: 'Downtown Madison',
        category: 'Festival',
        event_type: 'Community',
        cost: 'Free',
        organizer: 'City of Madison',
        url: 'https://www.madisonal.gov/events'
      }
    ];
  }

  /**
   * Get events that need syncing (based on last sync time)
   */
  async getSourcesNeedingSync(): Promise<EventSource[]> {
    const sourcesNeedingSync: EventSource[] = [];
    
    for (const [sourceName, config] of Object.entries(EVENT_FEEDS)) {
      if (!config.enabled) continue;
      
      // For now, assume all sources need syncing
      // In a real implementation, you'd check last_sync timestamps
      sourcesNeedingSync.push(sourceName as EventSource);
    }
    
    return sourcesNeedingSync;
  }
}

export const rocketCityEventsService = new RocketCityEventsService();
export default rocketCityEventsService;
